import { useLocalization } from "@progress/kendo-react-intl";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ListView, ListViewEvent, ListViewFooter, ListViewHeader, ListViewItemProps } from "@progress/kendo-react-listview";
import { SvgIcon } from "@progress/kendo-react-common";
import { Avatar } from "@progress/kendo-react-layout";
import { checkboxIcon, saveIcon, searchIcon, starIcon } from "@progress/kendo-svg-icons";
import { TextBox, TextBoxChangeEvent, TextBoxProps } from "@progress/kendo-react-inputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, GridCustomCellProps, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { loadingPanel } from "../../LoadingPanel";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { end } from "@progress/kendo-react-dateinputs";




/**
 * ConfigDevicesBinding is a React component that manages the binding of devices
 * within a list view. It handles the fetching and displaying of devices,
 * device bindings, and groups, as well as user interactions such as device
 * selection, filtering, and updating device information. The component
 * communicates with a server for data retrieval and updating using access tokens
 * for authorization. It also provides a user interface for managing device
 * bindings, including a search feature and save functionality.
 */

export const ConfigDevicesBinding = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;


    const [devicesList, setDevicesList] = useState<any>(null);
    const [counterActiveDevices, setCounterActiveDevices] = useState(null);
    //const [counterInactiveDevices, setCounterInactiveDevices] = useState(null);
    const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
    const [deviceInfoSelected, setDeviceInfoSelected] = useState([]);
    const [deviceMacSelected, setDeviceMacSelected] = useState('');
    const [deviceTypeName, setDeviceTypeName] = useState('');
    const [devicebattery, setDevicebattery] = useState('');
    const [deviceNameSelected, setDeviceNameSelected] = useState<TextBoxProps["value"]>('');
    const [searchFilterValue, setSearchFilterValue] = useState('');

    const [listViewData, setListViewData] = useState(devicesList);

    const [devicesBinding, setDevicesBinding] = useState<any>([]);
    const [lastFromBinding, setLastFromBinding] = useState<any>('');
    const [lastToBinding, setLastToBinding] = useState<Date | any>();
    const [closedBind, setClosedBind] = useState<boolean>(false);


    const [visible, setVisible] = useState<boolean>(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [updatedMessage, setUpdatedMessage] = useState(false);


    const localizationService = useLocalization();

    const [instance, accessTokenRequest] = AccessToken();

    let languageId = localStorage.getItem('languageId');

    const userType = localStorage.getItem('userType');

    const [loading, setLoading] = useState(false);
    const devicesListContainerRef = useRef<HTMLDivElement | null>(null);
    const listScrollTopRef = useRef(0);

    const getListScrollElement = () => {
        const container = devicesListContainerRef.current;
        if (!container) {
            return null;
        }

        return (container.querySelector('.k-listview-content') as HTMLElement | null) ?? container;
    };

    const rememberListScrollPosition = () => {
        const scrollElement = getListScrollElement();
        if (!scrollElement) {
            return;
        }

        listScrollTopRef.current = scrollElement.scrollTop;
    };

    const restoreListScrollPosition = () => {
        const savedScrollTop = listScrollTopRef.current;

        const applyScroll = () => {
            const currentScrollElement = getListScrollElement();
            if (!currentScrollElement) {
                return;
            }

            currentScrollElement.scrollTop = savedScrollTop;
        };

        applyScroll();
        requestAnimationFrame(() => {
            applyScroll();

            // First selection also changes layout; apply twice to avoid initial jump-to-top.
            requestAnimationFrame(() => {
                applyScroll();
            });
        });
    };

    const getDeviceList = async () => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/list`, {
                headers
            })
                .then((res: any) => {

                    if (res == '' || res?.data?.length == 0) {
                        setLoading(false);
                        return
                    }

                    const data: any = res?.data
                    setCounterActiveDevices(data?.data?.length);
                    setDevicesList(data?.data);

                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                    if (!error) {
                        setTimeout(() => {
                            setError(false);
                        }, 3000);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList()
    }, [])

    const convertToLocaleString = (dateStr: string): string => {
        const [day, month, yearAndTime] = dateStr.split('/');
        const [year, time] = yearAndTime.split(' ');

        const isoString = `${year}-${month}-${day}T${time}Z`;
        const date = new Date(isoString);

        return date.toLocaleString('it-IT', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    };


    const getDevicesBindingList = async (deviceMac: boolean) => {

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/bindings/${deviceMac}`, {
                headers
            })
                .then((res) => {
                    const data: any = res?.data?.data

                    if (data.length > 0) {

                        data?.forEach((binding: any) => {

                            if (binding?.startDate == null) {
                                binding.startDate = localizationService.toLanguageString('binding.date_not_found', 'NOT_FOUND')
                                return
                            } else {
                                const tempStartDate = binding?.startDate?.split('T')[0]
                                const formattedStartDate = convertToLocaleString(tempStartDate)
                                binding.startDate = formattedStartDate.replace(', ', ' ')
                            }

                            if (binding?.endDate == null) {
                                binding.endDate = localizationService.toLanguageString('binding.date_not_found', 'NOT_FOUND')
                                return
                            } else {
                                const tempEndDate = binding?.endDate?.split('T')[0]
                                const formattedEndDate = convertToLocaleString(tempEndDate)
                                binding.endDate = formattedEndDate.replace(', ', ' ')

                            }
                        })
                        if (data[0]?.endDate?.includes('9999-12-31') || data[0]?.endDate?.includes('31/12/9999')) {
                            setClosedBind(false)
                        } else {
                            setClosedBind(true)
                        }

                        data[0].recent = 'recent'

                        setDevicesBinding(data);

                        let lastDataBinding: any = data[0]?.startDate
                        setLastFromBinding(lastDataBinding);

                        let toDataBinding: any = data[0]?.endDate
                        setLastToBinding(toDataBinding);
                    } else {
                        setDevicesBinding([]);
                    }
                    //setLoading(false);
                }).catch((data) => {

                    setErrorMessage(data?.message)
                    setError(data?.message);
                    if (!errorMessage) {
                        setTimeout(() => {
                            setErrorMessage(false);
                        }, 5000);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const applyDeviceSearchFilter = (value: string, sourceList: any[] = []) => {
        if (!value) {
            setListViewData(sourceList);
            return;
        }

        const normalizedValue = value.toLocaleLowerCase();
        const newData = sourceList.filter((device: any) => {
            let match = false;
            for (const property in device) {
                if ((device['name']?.toString() && device['name']?.toString().toLocaleLowerCase().indexOf(normalizedValue) >= 0) || (device['mac']?.toString() && device['mac']?.toString().toLocaleLowerCase().indexOf(normalizedValue) >= 0)) {
                    match = true;
                }
            }
            return match;
        });

        setListViewData(newData);
    };

    const onFilterChange = (ev: any) => {
        const value = ev.value ?? '';
        setSearchFilterValue(value);
        applyDeviceSearchFilter(value, devicesList ?? []);
    };

    const MyHeader = () => {
        return (
            <>
                <ListViewHeader
                    style={{ color: "#018CA5", fontSize: 14 }}
                    className="pl-3 pb-2 pt-2"
                >
                    {localizationService.toLanguageString('configuration.devices.devices_list', 'NOT FOUND')} ({counterActiveDevices})
                </ListViewHeader>
            </>
        );
    };

    const MyFooter = () => {
        let messages = 0;
        devicesList.map((i: any) => {
            messages = messages + i.messages;
        });
        return (
            <ListViewFooter
                style={{ color: "#018CA5", fontSize: 14 }}
                className="pl-3 pb-2 pt-2"
            >
                {/*{counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')}*/}
            </ListViewFooter>
        );
    };

    const toggleDialog = (item?: any) => {
        setDeviceToDelete(item);
        setVisible(!visible);
    };

    const deleteDevice = () => {
        console.log(deviceToDelete);
    }

    // called on click device
    const getDeviceInfoOnClick = (item: any) => {
        rememberListScrollPosition();
        setDeviceInfoSelected(item)
        setDeviceMacSelected(item.mac);
        setDeviceNameSelected(item.name);
        setDeviceTypeName(item.deviceTypeName);
        setDevicebattery(item.battery);
        getGroupList();
        getDevicesBindingList(item.mac)
        //setDeviceVersionSelected(item.deviceVersion); // if exist...TBD
    }

    useEffect(() => {
        if (deviceMacSelected) {
            restoreListScrollPosition();
        }
    }, [deviceMacSelected]);

    const DevicesRender = (props: ListViewItemProps) => {
        let item = props.dataItem;
        return (
            <div
                className="k-listview-item row p-2 border-bottom align-middle"
                style={{ margin: 0, boxShadow: 'none' }}
            >
                <div
                    className="col-9"
                    style={{ cursor: 'pointer', color: "#018CA5" }}
                    onClick={(e) => {
                        getDeviceInfoOnClick(item);
                        const title = e.currentTarget.querySelector('.device-binding-name-h2') as HTMLElement | null;
                        title?.focus({ preventScroll: true });
                    }}
                >
                    <h2
                        className={`device-binding-name-h2${deviceMacSelected === item.mac ? ' is-selected' : ''}`}
                        tabIndex={0}
                        style={{
                            fontSize: 16,
                            padding: '10px',
                            cursor: 'pointer',
                        }}
                    >
                        {item.name} ({item.mac})
                    </h2>
                </div>
            </div>
        );
    };

    const scrollHandler = (event: ListViewEvent) => {
        const e = event.nativeEvent;
        if (e.target.scrollTop + 10 >= e.target.scrollHeight - e.target.clientHeight) {
            const moreData = devicesList.splice(0, 6);
            if (moreData.length > 0) {
                setDevicesList(devicesList.concat(moreData));
            }
        }
    };

    const handleChangeDeviceName = useCallback((event: TextBoxChangeEvent) => {
        setDeviceNameSelected(event.target.value);
    }, []);

    const getGroupList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/list`, { headers })
                .then((res) => {
                    const data = res.data.data
                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const CustomCell = (props: GridCustomCellProps) => {
        const tdProps = props.tdProps ?? {};
        return (
            <td
                {...tdProps}
                style={{
                    ...(tdProps.style ?? {}),
                    fontSize: '14px',
                }}
            >
                {props.children}
            </td>
        );

    };

    const formatDate = (date: any) => {
        const pad = (n: any) => n.toString().padStart(2, '0');

        const day = pad(date.getUTCDate());
        const month = pad(date.getUTCMonth() + 1); // i mesi partono da 0
        const year = date.getUTCFullYear();

        const hours = pad(date.getUTCHours());
        const minutes = pad(date.getUTCMinutes());
        const seconds = pad(date.getUTCSeconds());

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    const saveDevice = async (deviceMac: any, deviceName: any, startDate: any, endDate?: any) => {

        setLoading(true);

        let currentStartDate = new Date()


        let params = {}

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            params = {
                deviceMac,
                deviceName,
                startDate: formatDate(currentStartDate)
            };

            try {
                await axios.post(`${SERVER_URL}/device/binding/update`, params, { headers })

                setDeviceNameSelected('')
                setUpdatedMessage(true);
                setClosedBind(true);

                getDevicesBindingList(deviceMac);
                getDeviceList();

                // Scroll to top
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setLoading(false)

                if (!updatedMessage) {
                    setTimeout(() => {
                        setUpdatedMessage(false);
                    }, 3000);
                }
            } catch (error: any) {
                setErrorMessage(error);
                if (error) {
                    setTimeout(() => {
                        setErrorMessage(false);
                    }, 3000);
                }
                setLoading(false)
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    const handleCloseBinding = (deviceSelected: any) => {

        devicesBinding.map((device: any) => {
            if (device.startDate === deviceSelected.startDate) {
                device.endDate = new Date();
            }
        })
        setDevicesBinding([...devicesBinding]);
        setDeviceNameSelected('');
        closeDeviceBinding(deviceSelected, true)
    }

    const closeDeviceBinding = async (deviceSelected: any, onlyClose?: boolean) => {

        setLoading(true);

        let newDevice: any = JSON.parse(JSON.stringify(deviceSelected))

        const endDateFormatted = new Date()

        const data = {
            deviceMac: newDevice.mac,
            deviceName: newDevice.name ?? deviceNameSelected,
            startDate: newDevice.startDate,
            endDate: formatDate(endDateFormatted)
        };

        const commonOperations = async (accessToken: any) => {

            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.delete(`${SERVER_URL}/device/binding/end`, { data, headers })

                setLastToBinding('');
                setLastFromBinding('');
                setClosedBind(true);

                if (lastToBinding == '31/12/9999 00:00:00' && !onlyClose) {
                    await saveDevice(deviceMacSelected, deviceNameSelected, lastFromBinding)
                } else {
                    getDevicesBindingList(newDevice.mac)
                    getGroupList();
                    getDeviceList();
                }

                setLoading(false);
            } catch (data: any) {
                setLoading(false);
                setError(data?.message);
            };
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const CloseBindingIconGrid = (propsGrid: GridCustomCellProps) => {
        const deviceSelected = propsGrid?.dataItem
        const tdProps = propsGrid.tdProps ?? {};
        const shouldShowClose = deviceSelected != null && deviceSelected?.recent == 'recent' && !closedBind;

        return (
            <td
                {...tdProps}
                style={{
                    ...(tdProps.style ?? {}),
                    textAlign: 'center'
                }}
            >
                {shouldShowClose && (
                    <span
                        className="k-icon k-font-icon k-i-unlink"
                        style={{ zoom: '1.5', cursor: 'pointer', color: '#018CA5' }}
                        onClick={() => handleCloseBinding(deviceSelected)}
                        title={localizationService.toLanguageString('configuration.devices.close_binding', 'NOT FOUND')}
                    />
                )}
            </td>
        )
    };

    useEffect(() => {
        applyDeviceSearchFilter(searchFilterValue, devicesList ?? []);
    }, [devicesList, searchFilterValue]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);


    return (
        <>
            {loading && loadingPanel}
            {devicesList?.length == 0 || devicesList == null &&
                // Mostra un messaggio o un altro contenuto se non ci sono dati
                <div style={{ textAlign: 'center', padding: '20px', border: '1px dashed #ccc' }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            <NotificationGroup
                style={{
                    right: 20,
                    bottom: 40,
                    position: "fixed",
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                    zIndex: 1000
                }}
            >
                <Fade enter={true} exit={true}>
                    {errorMessage && (
                        <Notification
                            type={{ style: "error", icon: true }}
                            onClose={() => setErrorMessage(false)}
                        >
                            <span>{errorMessage}</span>
                        </Notification>
                    )}
                </Fade>
                <Fade enter={true} exit={true}>
                    {updatedMessage && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            onClose={() => setUpdatedMessage(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.devices.updated_device', 'NOT FOUND')}</span>
                        </Notification>
                    )}
                </Fade>
            </NotificationGroup>
            {devicesList?.length > 0 &&
                <div
                    ref={scrollContainerRef}
                    className="flex-parent-element-device-binding"
                    style={{ overflow: 'hidden' }}
                >

                    {/* left panel */}
                    <div
                        className="flex-left-child-element"
                        ref={devicesListContainerRef}
                        style={{
                            width: "40%",
                            background: "#F7F7F7",
                            paddingLeft: "1%"
                        }}
                    >
                        <div
                            className="k-listview-item row p-2 align-middle"
                            style={{ display: 'block', margin: 0, }}
                        >
                            <fieldset style={{ display: 'flex' }}>
                                <TextBox
                                    style={{
                                        border: '2px solid #ccc',
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    value={searchFilterValue}
                                    onChange={onFilterChange}
                                    placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT FOUND')}
                                    prefix={() => (
                                        <SvgIcon icon={searchIcon} svgStyle={{ margin: '20px -5px 0px 5px' }} />
                                    )}
                                />
                            </fieldset>
                        </div>
                        <ListView
                            data={listViewData ?? devicesList}
                            item={DevicesRender}
                            style={{
                                background: "#F7F7F7",
                                height: '90%'
                            }}
                            header={MyHeader}
                            footer={MyFooter}
                        />
                        {visible && (
                            <Dialog
                                title={localizationService.toLanguageString('configuration.devices.remove_device', 'NOT FOUND')}
                                onClose={toggleDialog}
                            >
                                <div>
                                    <button
                                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                        style={{
                                            margin: '10px',
                                            width: '80px'
                                        }}
                                        onClick={toggleDialog}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT FOUND')}
                                    </button>
                                    <button
                                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                        style={{
                                            margin: '10px',
                                            width: '80px'
                                        }}
                                        onClick={() => {
                                            if (visible) {
                                                toggleDialog()
                                                deleteDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                    </div>

                    {/* right panel */}
                    {deviceMacSelected != '' &&
                        //<div className="flex-right-child-element" style={{ width: "35%", background: '#ffffff' }}>
                        <div
                            style={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                alignItems: 'center'
                            }}
                        >
                            {/* mac + name */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0, cursor: 'default' }}
                            >
                                <fieldset>
                                    <h1 className="k-h2" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.mac', 'NOT_FOUND')}</h1>
                                    <h5 className="k-h5" style={{ color: '#018CA5' }}>{deviceMacSelected != '' ? deviceMacSelected : localizationService.toLanguageString('configuration.devices.no_mac', 'NOT_FOUND')}</h5>

                                    <hr style={{ border: '1px solid #018CA5', width: '100%' }} />

                                    <div className="k-h5" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}</div>
                                    <TextBox
                                        style={{ color: "#018CA5" }}
                                        value={deviceNameSelected ?? ''}
                                        onChange={handleChangeDeviceName}
                                        placeholder={localizationService.toLanguageString('configuration.devices.enter_device_name', 'NOT_FOUND')}
                                        disabled={userType == 'StandardOnlyLocator'}
                                    />
                                </fieldset>
                            </div>

                            <br />

                            {/* type */}
                            <div className="k-listview-item row p-1 align-middle" style={{ cursor: 'default' }}>
                                <div className="col-2" >
                                    <Avatar type="image" style={{ background: "#018CA5", border: 'none' }}>
                                        <SvgIcon icon={starIcon} style={{ verticalAlign: 'baseline', margin: '8px' }} />
                                    </Avatar>
                                </div>
                                <div className="col-9" >
                                    <h1
                                        style={{ fontSize: 16, color: "#018CA5", verticalAlign: 'bottom', display: 'inline-flex' }}
                                        className="text-uppercase"
                                    >
                                        {deviceTypeName != '' ? deviceTypeName : ''}
                                    </h1>
                                </div>
                            </div>

                            <br />

                            <br />

                            {/* bindings */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <div className="k-h5" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.all_bindings', 'NOT_FOUND')}</div>

                            </div>

                            {devicesBinding.length > 0 &&
                                <Grid
                                    className="device-binding-grid"
                                    style={{
                                        margin: '.5rem',
                                        height: '400px',                                         
                                    }}
                                    data={devicesBinding}                       
                                >
                                    <GridColumn
                                        field="serial"
                                        title={localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}
                                        width="150px"
                                    />
                                    <GridColumn
                                        field="startDate"
                                        title={localizationService.toLanguageString('configuration.devices.from', 'NOT_FOUND')}
                                        format='{0:dd/MM/yyyy HH:mm:ss}'
                                        width="150px"
                                        cells={{ data: CustomCell }}
                                    />
                                    <GridColumn
                                        field="endDate"
                                        title={localizationService.toLanguageString('configuration.devices.to', 'NOT_FOUND')}
                                        format='{0:dd/MM/yyyy HH:mm:ss}'
                                        width="150px"
                                        cells={{ data: CustomCell }}
                                    />
                                    <GridColumn
                                        field="closeBinding"
                                        title={localizationService.toLanguageString('configuration.devices.close_binding', 'NOT_FOUND')}
                                        width="140px"
                                        cells={{ data: CloseBindingIconGrid }}
                                        hidden={userType == 'StandardOnlyLocator'}
                                    />
                                </Grid>
                            }

                            <br />

                            {/* save button */}
                            <div className="k-listview-item row p-4">
                                <Button
                                    type="button"
                                    themeColor={"primary"}
                                    className="save-devices-management-button"
                                    svgIcon={saveIcon}
                                    disabled={deviceNameSelected == '' || userType == 'StandardOnlyLocator'}
                                    onClick={(e: any) => {
                                        e.preventDefault();
                                        if (lastToBinding == '31/12/9999 00:00:00') {
                                            closeDeviceBinding(deviceInfoSelected)
                                        } else {
                                            saveDevice(deviceMacSelected, deviceNameSelected, lastFromBinding)
                                        }
                                    }}
                                >
                                    {localizationService.toLanguageString('configuration.devices.save', 'NOT_FOUND')}
                                </Button>
                            </div>

                        </div>
                    }
                </div>
            }
        </>
    )
}